import React from "react"

import type { MetricRecord } from "@/metrics/runtime"
import { useGetOrgRecordTransport } from "@/organization"
import { useSimulationOutputTransport } from "@/runs"
import { Button, Tooltip } from "@coval/ui"
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
} from "lucide-react"

import { ConversationViewer } from "@/components/transcript/ConversationViewer"
import type { WordHighlight } from "@/components/transcript/word-highlight"
import { useQueryParams } from "@/hooks/useQueryParams"
import type { Message } from "@/types/Transcript"

import { useTestDrawer } from "../contexts"

interface TranscriptDetailViewProps {
  currentIndex: number
  totalCount: number
  onNext: () => void
  onPrevious: () => void
  renderReviewSection: () => React.ReactNode
  highlightedMessages?: number[] | null
  highlightedWords?: WordHighlight[] | null
  metricRecordForAnnotations?: MetricRecord
}

export const TranscriptDetailView: React.FC<TranscriptDetailViewProps> = ({
  currentIndex,
  totalCount,
  onNext,
  onPrevious,
  renderReviewSection,
  highlightedMessages,
  highlightedWords,
  metricRecordForAnnotations,
}) => {
  const { selectedSimulationId, handleBackToList } = useTestDrawer()
  const simulationId = selectedSimulationId!
  const { data: orgRecord } = useGetOrgRecordTransport()
  const queryParams = useQueryParams()

  // Fetch simulation output
  const { data: simulationOutput, isLoading: isLoadingTranscript } =
    useSimulationOutputTransport(simulationId, { enabled: !!simulationId })

  const handleCopyId = () => {
    void navigator.clipboard.writeText(simulationId)
  }

  const handleOpenRun = () => {
    if (orgRecord?.slug && simulationOutput?.run_id) {
      window.open(
        queryParams.getHref(
          `/simulated/runs/${simulationOutput.run_id}/results/${simulationId}`,
          orgRecord.slug,
        ),
        "_blank",
      )
    }
  }

  const transcript = (simulationOutput?.transcript as Message[]) || []

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-4 py-3 border-b border-coval-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            icon
            variant="ghost"
            onClick={handleBackToList}
            tooltip="Back to list"
            className="text-coval-text-muted"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <span className="text-sm text-coval-text-muted font-mono">
              {simulationId.slice(0, 12)}
            </span>
            <Tooltip content="Copy ID">
              <button
                onClick={handleCopyId}
                aria-label="Copy ID"
                className="text-coval-text-muted hover:text-coval-text"
              >
                <Copy className="w-3.5 h-3.5" />
              </button>
            </Tooltip>
            {simulationOutput?.run_id && (
              <Tooltip content="Open Conversation">
                <button
                  onClick={handleOpenRun}
                  aria-label="Open conversation"
                  className="text-coval-text-muted hover:text-coval-text"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                </button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            icon
            variant="ghost"
            onClick={onPrevious}
            disabled={currentIndex === 0}
            tooltip="Previous"
            className="text-coval-text-muted"
          >
            <ChevronUp className="w-4 h-4" />
          </Button>
          <span className="text-sm text-coval-text-muted">
            {currentIndex + 1} of {totalCount}
          </span>
          <Button
            icon
            variant="ghost"
            onClick={onNext}
            disabled={currentIndex === totalCount - 1}
            tooltip="Next"
            className="text-coval-text-muted"
          >
            <ChevronDown className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto min-h-0 flex flex-col">
        {/* Review Section (provided by parent) */}
        {renderReviewSection()}

        {/* Transcript */}
        <div className="flex-1 min-h-0">
          <ConversationViewer
            transcript={transcript}
            showCopyButton
            isLoading={isLoadingTranscript}
            agentLabel="Your Agent"
            userLabel="Persona"
            highlightedMessages={highlightedMessages}
            highlightedWords={highlightedWords}
            metricRecordForAnnotations={metricRecordForAnnotations}
          />
        </div>
      </div>
    </div>
  )
}
